import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Send, RefreshCw } from 'lucide-react';
import LockCanvas from './LockCanvas';
import './Login.css';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [envelopeOut, setEnvelopeOut] = useState(false);
  const [shakeInput, setShakeInput] = useState(false);
  const timerRef = useRef(null);

  const startCountdown = () => {
    setCountdown(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown(v => {
        if (v <= 1) { clearInterval(timerRef.current); return 0; }
        return v - 1;
      });
    }, 1000);
  };

  const addRipple = (e) => {
    const btn = e.currentTarget;
    const r = btn.getBoundingClientRect();
    const s = Math.max(r.width, r.height) * 2;
    const span = document.createElement('span');
    span.className = 'btn-ripple';
    span.style.cssText = `width:${s}px;height:${s}px;left:${e.clientX - r.left - s / 2}px;top:${e.clientY - r.top - s / 2}px;`;
    btn.appendChild(span);
    setTimeout(() => span.remove(), 600);
  };

  const handleSend = async (e) => {
    addRipple(e);
    if (!email) {
      setShakeInput(true);
      setTimeout(() => setShakeInput(false), 500);
      return;
    }
    setEnvelopeOut(true);
    setLoading(true);
    await new Promise(r => setTimeout(r, 1800));
    setLoading(false);
    setSent(true);
    setEnvelopeOut(false);
    startCountdown();
  };

  const handleResend = async (e) => {
    addRipple(e);
    setLoading(true);
    await new Promise(r => setTimeout(r, 1400));
    setLoading(false);
    startCountdown();
  };

  return (
    <div className="auth-container">
      {/* ── Left panel ── */}
      <div className="auth-left">
        <LockCanvas />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent 70%,var(--bg-main))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,transparent 65%,var(--bg-main))', pointerEvents: 'none' }} />

        {/* Top bar */}
        <div style={{ position: 'absolute', top: 28, left: 32, right: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', animation: 'fadeUp 500ms both 300ms' }}>
          <button
            className="btn-ghost"
            onClick={() => navigate('/login')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <ArrowLeft size={14} />
            Back to Sign In
          </button>
          <div className="status-pill">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            SECURE RECOVERY
          </div>
        </div>

        {/* Bottom text */}
        <div style={{ position: 'absolute', bottom: 28, left: 32, animation: 'fadeUp 600ms both 800ms' }}>
          <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-.02em', lineHeight: 1.3 }}>
            Password recovery<br />
            <span style={{ color: 'var(--accent-primary)' }}>fully encrypted.</span>
          </p>
          <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
            {['256-bit AES', 'Zero-trust', 'TLS 1.3'].map(l => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-muted)' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-primary)' }} />
                {l}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="auth-right">
        <div className="ambient anim-breathe" style={{ position: 'absolute', width: 320, height: 320, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'radial-gradient(circle,rgba(var(--app-primary-color-rgb), 0.1) 0%,transparent 70%)', zIndex: 0 }} />

        <div className="nm-card" style={{ width: '100%', maxWidth: 400, padding: '40px 36px', position: 'relative', zIndex: 1, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', overflowX: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 180, height: 1, background: 'linear-gradient(90deg,transparent,var(--accent-primary),transparent)' }} />

          {!sent ? (
            /* ── REQUEST STATE ── */
            <>
              {/* Icon */}
              <div style={{ marginBottom: 24, animation: 'logoEntrance 600ms cubic-bezier(0.34,1.56,0.64,1) both 450ms' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16, margin: '0 auto',
                  background: 'rgba(var(--app-primary-color-rgb), 0.12)', border: '1px solid var(--border-light)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative',
                  animation: envelopeOut ? 'envelopeFly 1.8s ease-in forwards' : undefined,
                }}>
                  <Mail size={24} color="var(--accent-primary)" strokeWidth={1.5} />
                  <div style={{ position: 'absolute', inset: -8, borderRadius: 24, border: '1px solid rgba(var(--app-primary-color-rgb), 0.2)', animation: 'glowExpand 2.5s ease-in-out infinite' }} />
                </div>
              </div>

              <div style={{ animation: 'fadeUp 500ms both 550ms' }}>
                <h1 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-.025em', textAlign: 'center' }}>
                  Forgot Password?
                </h1>
                <p style={{ margin: '0 0 28px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.65 }}>
                  Enter your registered email and we'll send<br />secure reset instructions.
                </p>
              </div>

              <div className={`nm-field ${shakeInput ? 'anim-shake' : ''}`} style={{ marginBottom: 20, animation: 'fadeUp 500ms both 600ms' }}>
                <input
                  type="email"
                  placeholder=" "
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  id="fp-email"
                  autoComplete="email"
                  style={{ paddingLeft: 44 }}
                />
                <label htmlFor="fp-email" style={{ left: 44 }}>Registered email address</label>
                <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', zIndex: 2, pointerEvents: 'none' }}>
                  <Mail size={15} />
                </div>
              </div>

              <button
                className="btn-primary"
                onClick={handleSend}
                disabled={loading}
                style={{ width: '100%', height: 48, fontSize: 14, fontWeight: 600, animation: 'fadeUp 500ms both 650ms' }}
              >
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spinFast .7s linear infinite' }} /> Sending secure link…
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    Send Reset Link <Send size={14} />
                  </div>
                )}
              </button>

              <button
                className="btn-ghost"
                onClick={() => navigate('/login')}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 14, height: 38, animation: 'fadeUp 500ms both 700ms' }}
              >
                <ArrowLeft size={14} /> Back to Sign In
              </button>
            </>
          ) : (
            /* ── SUCCESS STATE ── */
            <div style={{ textAlign: 'center' }}>
              <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 24px', animation: 'glowExpand 700ms cubic-bezier(0.34,1.56,0.64,1) both' }}>
                <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'radial-gradient(circle,rgba(34,197,94,.18) 0%,transparent 70%)' }} />
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  animation: 'successPop 600ms cubic-bezier(0.34,1.56,0.64,1) both',
                  position: 'relative', zIndex: 1,
                }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" strokeDasharray="80" strokeDashoffset="0" style={{ animation: 'checkDraw 500ms ease-out both 300ms' }} />
                  </svg>
                </div>
              </div>

              <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-.02em', animation: 'fadeUp 500ms both 200ms' }}>
                Check your inbox
              </h2>
              <p style={{ margin: '0 0 4px', fontSize: 13, color: 'var(--text-secondary)', animation: 'fadeUp 500ms both 260ms' }}>
                Reset instructions sent to
              </p>
              <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--accent-primary)', fontWeight: 600, animation: 'fadeUp 500ms both 310ms' }}>
                {email}
              </p>

              {/* Countdown */}
              <div style={{ animation: 'fadeUp 500ms both 360ms' }}>
                <div style={{ height: 2, background: 'var(--border-light)', borderRadius: 2, overflow: 'hidden', marginBottom: 8 }}>
                  <div style={{ height: '100%', background: 'var(--accent-primary)', borderRadius: 2, width: `${(countdown / 60) * 100}%`, transition: 'width 1s linear' }} />
                </div>
                <p style={{ margin: '0 0 20px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
                  {countdown > 0 ? `Resend available in ${countdown}s` : 'You can resend the email now'}
                </p>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, animation: 'fadeUp 500ms both 410ms' }}>
                <button
                  className="btn-primary"
                  onClick={() => navigate('/login')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 46, fontSize: 14, fontWeight: 600 }}
                >
                  <ArrowLeft size={14} /> Back to Sign In
                </button>
                <button
                  className="btn-secondary"
                  onClick={handleResend}
                  disabled={countdown > 0 || loading}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 44, fontSize: 13, opacity: countdown > 0 ? 0.4 : 1 }}
                >
                  {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 14, height: 14, border: '1.5px solid rgba(var(--app-primary-color-rgb), 0.3)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spinFast .7s linear infinite' }} /> Sending…
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <RefreshCw size={14} /> Resend Email
                    </div>
                  )}
                </button>
              </div>

              <p style={{ margin: '18px 0 0', fontSize: 11, color: 'var(--text-muted)', animation: 'fadeUp 500ms both 460ms' }}>
                Didn't receive it? Check your spam folder.
              </p>
            </div>
          )}

          <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 180, height: 1, background: 'linear-gradient(90deg,transparent,var(--accent-primary),transparent)' }} />
        </div>
      </div>
    </div>
  );
}
